import { Fragment, useId, useMemo } from 'react'
import type { ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'

import {
  computeAscentDescentFromElevations,
  computeAscentDescentMeters,
  computeAverageCadence,
  computeAverageHeartRate,
  computeAveragePaceSecPerKm,
  computeAverageSpeedMetersPerSecond,
  computeMaxHeartRate,
  computeMaxSpeedMetersPerSecond,
  computeMovingTimeSec,
  computeTiming,
  computeTotalCalories,
  computeTotalDistanceMeters,
  formatTimestamp,
  getActiveElevationSource,
} from '@fit-editor/fit-core'
import type { Activity, ActivityFileId, TimeDisplayMode } from '@fit-editor/fit-core'

import { InlineProvenanceValue, ProvenanceStat } from '@/components/dashboard/provenance-stat'
import { useCurrentActivity } from '@/hooks/use-current-activity'
import { withCaloriesProvenance } from '@/lib/calories-provenance'
import { useTerrainOverlay } from '@/hooks/use-terrain-overlay'
import { getActivityTimeRange, getSportLabel } from '@/lib/activity-helpers'
import { useUnitFormatters } from '@/hooks/use-units'
import { formatCadence, formatCalories, formatDurationSeconds, formatHeartRate } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useActivityStore } from '@/store/activity-store'
import type { LoadedActivity } from '@/store/activity-store'

export interface ActivityDashboardProps {
  loaded: LoadedActivity
}

/** Placeholder fed to every compute function while the real activity is still loading, so every hook below can be called unconditionally (rules of hooks) without a real activity in hand yet. */
const EMPTY_ACTIVITY: Activity = {
  fileId: undefined,
  records: [],
  laps: [],
  sessions: [],
  events: [],
  activitySummary: undefined,
  otherMessages: {},
  preservedUnknownMessages: [],
  mixedMessageUnknownFields: [],
  decodeErrors: [],
}

/**
 * Shows real decoded/recalculated data for the selected activity, replaying
 * its edit history off the main thread (`useCurrentActivity`, Worker-routed
 * -- see that hook's doc comment for why a direct `currentActivity` call
 * here would re-run the full edit log over a potentially large `records`
 * array on the main thread). Shows a lightweight loading state rather than
 * stale/blank stats while that replay is in flight.
 *
 * The chevron in the header collapses the stat/file cards to a one-line
 * summary (distance, timer time, ascent -- provenance still shown), a
 * persisted UI pref; the workspace then lets the map take the room.
 */
export function ActivityDashboard({ loaded }: ActivityDashboardProps) {
  const timeDisplayMode = useActivityStore((s) => s.ui.timeDisplayMode)
  const setTimeDisplayMode = useActivityStore((s) => s.setTimeDisplayMode)
  const collapsed = useActivityStore((s) => s.ui.dashboardCollapsed === true)
  const setDashboardCollapsed = useActivityStore((s) => s.setDashboardCollapsed)
  const detailsId = useId()
  const fmt = useUnitFormatters()

  const { result } = useCurrentActivity(loaded.history, { keepPreviousWhileLoading: true })
  const activity = result?.activity ?? EMPTY_ACTIVITY

  const timing = useMemo(() => computeTiming(activity), [activity])
  const distance = useMemo(() => computeTotalDistanceMeters(activity.records), [activity])
  // An active `setElevation` edit means the elevation profile came (wholly
  // or partly) from a terrain model, so ascent/descent are shown as
  // Estimated rather than Recalculated.
  const elevationEstimated = useMemo(
    () => getActiveElevationSource(loaded.history) != null,
    [loaded.history],
  )
  const { ascent, descent } = useMemo(
    () => computeAscentDescentMeters(activity.records, { elevationEstimated }),
    [activity, elevationEstimated],
  )
  const avgSpeed = useMemo(
    () => computeAverageSpeedMetersPerSecond(distance, timing.timerTimeSec),
    [distance, timing],
  )
  const avgPace = useMemo(
    () => computeAveragePaceSecPerKm(distance, timing.timerTimeSec),
    [distance, timing],
  )
  const maxSpeed = useMemo(() => computeMaxSpeedMetersPerSecond(activity.records), [activity])
  const movingTime = useMemo(() => computeMovingTimeSec(activity), [activity])
  const avgHeartRate = useMemo(() => computeAverageHeartRate(activity.records), [activity])
  const maxHeartRate = useMemo(() => computeMaxHeartRate(activity.records), [activity])
  const avgCadence = useMemo(() => computeAverageCadence(activity), [activity])
  const calories = useMemo(
    () => withCaloriesProvenance(computeTotalCalories(activity), loaded.history),
    [activity, loaded.history],
  )

  // Terrain-model ascent/descent, only once the (opt-in, session-only)
  // terrain overlay has been loaded for this activity. `overlay.values` is
  // index-aligned to the *current* activity's records, so this follows
  // edits exactly like the device-based values next to it.
  const { overlay: terrainOverlay } = useTerrainOverlay(loaded)
  const terrainAscentDescent = useMemo(
    () =>
      terrainOverlay != null ? computeAscentDescentFromElevations(terrainOverlay.values) : null,
    [terrainOverlay],
  )
  const timeRange = useMemo(() => getActivityTimeRange(activity), [activity])
  const sport = getSportLabel(activity)

  if (result == null) {
    return (
      <p className="text-muted-foreground flex items-center gap-2 text-sm">
        Loading &quot;{loaded.fileName}&quot;…
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-1.5">
          <button
            type="button"
            aria-label="Activity data"
            aria-expanded={!collapsed}
            aria-controls={detailsId}
            title={collapsed ? 'Expand activity data' : 'Collapse activity data'}
            onClick={() => setDashboardCollapsed(!collapsed)}
            className={cn(
              'text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring flex h-7 w-7 shrink-0 items-center justify-center rounded-md outline-none focus-visible:ring-2',
              !collapsed && 'mt-0.5',
            )}
          >
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', collapsed && '-rotate-90')}
              aria-hidden="true"
            />
          </button>
          {collapsed ? (
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
              {/* Long names without break opportunities (e.g. `Morning_Run_...`) wrap rather than widen the workspace. */}
              <h1 className="text-foreground min-w-0 text-base font-semibold [overflow-wrap:anywhere]">
                {loaded.fileName}
              </h1>
              <CompactSummary
                items={[
                  <span key="sport" className="text-foreground font-medium">
                    {sport}
                  </span>,
                  <InlineProvenanceValue
                    key="distance"
                    label="Distance"
                    value={distance}
                    format={fmt.distance}
                  />,
                  <span key="timer" className="inline-flex gap-1.5">
                    <span className="text-muted-foreground">Timer time</span>
                    <span className="text-foreground font-medium">
                      {formatDurationSeconds(timing.timerTimeSec)}
                    </span>
                  </span>,
                  <InlineProvenanceValue
                    key="ascent"
                    label="Ascent"
                    value={ascent}
                    format={fmt.elevation}
                  />,
                ]}
              />
            </div>
          ) : (
            <div className="min-w-0">
              <h1 className="text-foreground text-lg font-semibold [overflow-wrap:anywhere]">
                {loaded.fileName}
              </h1>
              <p className="text-muted-foreground text-sm">
                Sport: <span className="text-foreground font-medium">{sport}</span>
              </p>
            </div>
          )}
        </div>
        <TimeDisplayToggle mode={timeDisplayMode} onChange={setTimeDisplayMode} />
      </header>

      {/* Hidden (not unmounted) when collapsed, so `aria-controls` always points at it. */}
      <div id={detailsId} hidden={collapsed} className="flex flex-col gap-2">
        <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6">
          <PlainStat
            label="Start"
            value={timeRange.start ? formatTimestamp(timeRange.start, timeDisplayMode) : 'Unknown'}
          />
          <PlainStat
            label="End"
            value={timeRange.end ? formatTimestamp(timeRange.end, timeDisplayMode) : 'Unknown'}
          />
          <PlainStat label="Elapsed time" value={formatDurationSeconds(timing.elapsedTimeSec)} />
          <PlainStat label="Timer time" value={formatDurationSeconds(timing.timerTimeSec)} />
          <ProvenanceStat
            asDefinition
            label="Moving time"
            value={movingTime}
            format={formatDurationSeconds}
          />
          <PlainStat label="Laps" value={String(activity.laps.length)} />
          <PlainStat label="Pauses" value={String(timing.pauses.length)} />
        </dl>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6">
          <ProvenanceStat label="Distance" value={distance} format={fmt.distance} />
          <ProvenanceStat label="Ascent" value={ascent} format={fmt.elevation} />
          <ProvenanceStat label="Descent" value={descent} format={fmt.elevation} />
          {terrainAscentDescent != null && (
            <>
              <ProvenanceStat
                label="Terrain ascent"
                value={terrainAscentDescent.ascent}
                format={fmt.elevation}
                hint="From terrain data (Mapterhorn)"
              />
              <ProvenanceStat
                label="Terrain descent"
                value={terrainAscentDescent.descent}
                format={fmt.elevation}
                hint="From terrain data (Mapterhorn)"
              />
            </>
          )}
          <ProvenanceStat label="Avg speed" value={avgSpeed} format={fmt.speed} />
          <ProvenanceStat label="Avg pace" value={avgPace} format={fmt.pace} />
          <ProvenanceStat label="Max speed" value={maxSpeed} format={fmt.speed} />
          <ProvenanceStat label="Avg heart rate" value={avgHeartRate} format={formatHeartRate} />
          <ProvenanceStat label="Max heart rate" value={maxHeartRate} format={formatHeartRate} />
          <ProvenanceStat label="Avg cadence" value={avgCadence} format={formatCadence} />
          <ProvenanceStat label="Calories" value={calories} format={formatCalories} />
        </div>

        <DeviceInfoCard fileId={activity.fileId} />
      </div>

      {activity.decodeErrors.length > 0 && (
        <p className="text-destructive text-sm">
          This file had {activity.decodeErrors.length}{' '}
          {activity.decodeErrors.length === 1 ? 'decode issue' : 'decode issues'} — see details in
          the sidebar.
        </p>
      )}
    </div>
  )
}

/** The collapsed dashboard's one-line summary: items separated by middle dots. */
function CompactSummary({ items }: { items: ReactNode[] }) {
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      {items.map((item, i) => (
        <Fragment key={i}>
          {i > 0 && (
            <span className="text-muted-foreground" aria-hidden="true">
              ·
            </span>
          )}
          {item}
        </Fragment>
      ))}
    </p>
  )
}

function PlainStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-border bg-card flex min-w-0 flex-col gap-0.5 rounded-md border px-2.5 py-1.5">
      <dt className="text-muted-foreground truncate text-xs">{label}</dt>
      <dd className="text-card-foreground text-sm font-semibold [overflow-wrap:anywhere]">
        {value}
      </dd>
    </div>
  )
}

function DeviceInfoCard({ fileId }: { fileId: ActivityFileId | undefined }) {
  if (fileId == null) {
    return (
      <p className="text-muted-foreground text-sm">No device information available in this file.</p>
    )
  }

  const items: [string, string][] = [
    ['Manufacturer', fileId.manufacturer != null ? String(fileId.manufacturer) : 'Unknown'],
    ['Product', fileId.product != null ? String(fileId.product) : 'Unknown'],
    ['Serial number', fileId.serialNumber != null ? String(fileId.serialNumber) : 'Unknown'],
    [
      'File created',
      fileId.timeCreated instanceof Date ? formatTimestamp(fileId.timeCreated, 'local') : 'Unknown',
    ],
  ]
  // One compact line (wraps on narrow widths) rather than a card of cards.
  return (
    <dl className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 px-0.5 text-xs">
      {items.map(([label, value]) => (
        <div key={label} className="flex min-w-0 gap-1.5">
          <dt>{label}</dt>
          <dd className="text-foreground font-medium [overflow-wrap:anywhere]">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function TimeDisplayToggle({
  mode,
  onChange,
}: {
  mode: TimeDisplayMode
  onChange: (mode: TimeDisplayMode) => void
}) {
  const options: { mode: TimeDisplayMode; label: string }[] = [
    { mode: 'local', label: 'Local time' },
    { mode: 'utc', label: 'UTC' },
  ]

  return (
    <div
      role="group"
      aria-label="Time display"
      className="border-border inline-flex overflow-hidden rounded-md border"
    >
      {options.map((option) => (
        <button
          key={option.mode}
          type="button"
          aria-pressed={mode === option.mode}
          onClick={() => onChange(option.mode)}
          className={
            mode === option.mode
              ? 'bg-primary text-primary-foreground px-3 py-1 text-xs font-medium'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground bg-transparent px-3 py-1 text-xs font-medium'
          }
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}
